/** @format */

import {ChangeEvent, useCallback, useEffect, useState} from 'react';

const MAX_LENGTH = 100;

interface ReturnValues {
  edit: boolean;
  innerValue: string;
  onStartEditTitle: () => void;
  onStopEditTitle: () => void;
  onChangeInnerValue: (event: ChangeEvent<HTMLInputElement>) => void;
}

const useTitle = (title: string, onChangeTitle: (value: string) => void): ReturnValues => {
  const [edit, setEdit] = useState<boolean>(false);
  const [innerValue, setInnerValue] = useState<string>(title);

  const onStopEditTitle = useCallback(() => {
    const value = innerValue.trim();
    if (value.length > MAX_LENGTH) {
      return;
    }
    setEdit(false);
    onChangeTitle(value);
  }, [innerValue, onChangeTitle]);

  const handleEnter = useCallback(
    (e: KeyboardEvent) => {
      if (edit && e.key === 'Enter') {
        onStopEditTitle();
      }
    },
    [edit, onStopEditTitle],
  );

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (edit && e.key === 'Escape') {
        setInnerValue(title);
        setEdit(false);
      }
    },
    [edit, title],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEnter);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEnter);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleEnter, handleEscape]);

  const onStartEditTitle = () => {
    setEdit(true);
  };

  const onChangeInnerValue = (event: ChangeEvent<HTMLInputElement>) => {
    setInnerValue(event.target.value);
  };

  return {
    edit,
    innerValue,
    onChangeInnerValue,
    onStartEditTitle,
    onStopEditTitle,
  };
};

export default useTitle;
